import Phaser from "phaser";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, NineSlicePanel } from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import { CONTENT_PANEL_STYLE, PRIMARY_BUTTON_STYLE } from "./uiTokens.js";
import { PALETTE_NUM } from "../themes/palette.js";
import { DEPTH } from "../objects/depthLayers.js";
import { dropdownTriggerLabel } from "./directionalUi.js";

/** 펼침 중 전체화면 dim 캐처의 불투명도 — 바깥(탭/탭바)을 어둡게 깔아 펼친 옵션 리스트를 강조한다. */
export const CATCHER_DIM_ALPHA = 0.5;

export interface DropdownOptions<T extends string> {
  readonly width: number;
  readonly options: readonly T[];
  readonly current: T;
  /** 값 → 표시 라벨. 언어 변경 시 refreshLabels()로 재구성. */
  readonly labelOf: (value: T) => string;
  readonly onSelect: (value: T) => void;
  /** 트리거 화살표 위치 방향 — 기본 ltr(라벨 우측). 언어 전환 시 setDirection()으로 갱신. */
  readonly dir?: TextDirection;
  /** 트리거 버튼 높이(px) — 기본 36. 좁은 헤더 행(전투 탭 정렬 등)에 인라인 배치 시 주변 텍스트 높이와 통일하려고 낮춘다. */
  readonly triggerHeight?: number;
}

/** 드롭다운 옵션 라벨 — 현재 선택 항목은 체크박스(☑), 나머지는 빈 박스(☐)를 앞에 붙인다(순수, 테스트용 export). */
export function dropdownOptionLabel(label: string, selected: boolean): string {
  return `${selected ? "☑" : "☐"} ${label}`;
}

/**
 * 제네릭 선택 드롭다운 — @yjsgames/phaser-ui에 드롭다운이 없어 신규. (LanguageDropdown을 일반화한 것.)
 * 트리거 버튼(현재값 라벨) 클릭 시 아래로 옵션 버튼을 펼치고, 항목 선택 시 닫으며 onSelect 콜백.
 * 펼침 시 ① 화면 전체 dim 캐처(바깥 클릭 닫기 — 모달 dim과 동일 UX) ② 옵션 리스트 뒤 강조 dim + frame(9-slice)을 깐다.
 * 각 옵션은 현재 선택 항목을 체크박스(☑/☐)로 표시한다. Container라 모달 contentContainer.add(dropdown)으로 부착한다.
 */
export class Dropdown<T extends string> extends Phaser.GameObjects.Container {
  private readonly trigger: NineSliceButton;
  private readonly optionButtons: { readonly value: T; readonly btn: NineSliceButton }[] = [];
  /** 펼침 중 화면 전체를 덮는 dim + 클릭 캐처 — 옵션 리스트 바깥 클릭 시 닫는다(모달 dim 동일 UX). */
  private readonly catcher: Phaser.GameObjects.Rectangle;
  /** 펼친 옵션 리스트 뒤 강조 dim(반투명) — 리스트 영역만 한 단계 더 어둡게. */
  private readonly backDim: Phaser.GameObjects.Rectangle;
  /** 옵션 리스트 영역 9-slice 프레임 — 리스트 높이만큼. */
  private readonly frame: NineSlicePanel;
  private readonly labelOf: (value: T) => string;
  private current: T;
  /** 트리거 화살표 위치 방향 — RTL이면 화살표가 라벨 왼쪽. */
  private dir: TextDirection;
  private expanded = false;
  /** 펼치기 전의 휴지 depth — 닫을 때 이 값으로 복원한다(0 하드코딩 금지: 탭 콘텐츠 뒤로 깔림 방지). */
  private restDepth = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: DropdownOptions<T>) {
    super(scene, x, y);
    this.labelOf = opts.labelOf;
    this.current = opts.current;
    this.dir = opts.dir ?? "ltr";

    const rowH = 40;
    const triggerH = opts.triggerHeight ?? 36;
    this.trigger = new NineSliceButton(scene, 0, 0, {
      style: PRIMARY_BUTTON_STYLE,
      label: dropdownTriggerLabel(opts.labelOf(opts.current), this.dir),
      widthOverride: opts.width,
      heightOverride: triggerH,
    });
    this.trigger.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.toggle());
    this.add(this.trigger);

    // 전체화면 dim 캐처 — 펼침 중 바깥(옵션 밖) 클릭 시 닫기. scene에 직접 추가하므로
    // 부모 Container 좌표와 무관하게 화면 절대 좌표(sw/2, sh/2)를 사용한다.
    const sw = scene.scale.width;
    const sh = scene.scale.height;
    this.catcher = scene.add
      .rectangle(sw / 2, sh / 2, sw, sh, PALETTE_NUM.overlayDim, CATCHER_DIM_ALPHA)
      .setOrigin(0.5)
      .setActive(false)
      .setVisible(false)
      .setInteractive();
    this.setRectInputEnabled(this.catcher, false);
    this.catcher.on("pointerdown", () => this.collapse());
    // W1: 뷰포트 리사이즈(모바일 회전 등) 시 catcher가 화면 전체를 계속 덮도록 위치·크기 갱신.
    const onResize = () => {
      const nw = scene.scale.width;
      const nh = scene.scale.height;
      this.catcher.setPosition(nw / 2, nh / 2).setSize(nw, nh);
    };
    scene.scale.on("resize", onResize);
    this.once("destroy", () => {
      scene.scale.off("resize", onResize);
      // 펼친 채(모달 경로) destroy되면 catcher가 부모 모달 children에 남아 부모 일괄 destroy와 이중 destroy될 수 있다
      // → 먼저 부모에서 분리(destroy 억제 false)한 뒤 Dropdown이 직접 destroy한다. 비모달(scene 직속)이면 parentContainer 없음.
      this.catcher.parentContainer?.remove(this.catcher, false);
      this.catcher.destroy();
    });

    // catcher는 scene에 직접 추가(Container 외부) — 부모 좌표 의존 없이 화면 전체를 덮는다.
    // 펼친 옵션 리스트 영역(첫 옵션 y=rowH ~ 마지막 y=n*rowH) 뒤 강조 dim + frame을 옵션 버튼보다 먼저 add(아래 레이어).
    const n = opts.options.length;
    const listCenterY = ((1 + n) * rowH) / 2;
    const listH = (n - 1) * rowH + 34;
    this.backDim = scene.add
      .rectangle(0, listCenterY, opts.width + 20, listH + 20, PALETTE_NUM.overlayDim, 0.6)
      .setOrigin(0.5)
      .setVisible(false);
    this.add(this.backDim);
    this.frame = new NineSlicePanel(scene, 0, listCenterY, {
      style: CONTENT_PANEL_STYLE,
      width: opts.width + 12,
      height: listH + 12,
      originX: 0.5,
      originY: 0.5,
    });
    this.frame.setVisible(false);
    this.add(this.frame);

    opts.options.forEach((value, i) => {
      const btn = new NineSliceButton(scene, 0, (i + 1) * rowH, {
        style: PRIMARY_BUTTON_STYLE,
        label: dropdownOptionLabel(opts.labelOf(value), value === opts.current),
        widthOverride: opts.width,
        heightOverride: 34,
      });
      btn.setVisible(false);
      btn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
        this.collapse();
        this.setCurrent(value);
        opts.onSelect(value);
      });
      this.optionButtons.push({ value, btn });
      this.add(btn);
    });
  }

  /** 현재 선택값 갱신 — 트리거 라벨 + 옵션 체크박스 재표시 (외부 동기화용, onSelect 미호출). */
  setCurrent(value: T): void {
    this.current = value;
    this.trigger.setLabel(dropdownTriggerLabel(this.labelOf(value), this.dir));
    this.refreshOptionChecks();
  }

  /** 언어 변경 등으로 옵션 라벨 자체가 바뀔 때 트리거+옵션 라벨 일괄 재구성(체크박스 유지). */
  refreshLabels(): void {
    this.trigger.setLabel(dropdownTriggerLabel(this.labelOf(this.current), this.dir));
    this.refreshOptionChecks();
  }

  /** 옵션 버튼 라벨을 현재 선택 기준 체크박스(☑/☐) 반영해 재구성. */
  private refreshOptionChecks(): void {
    for (const { value, btn } of this.optionButtons)
      btn.setLabel(dropdownOptionLabel(this.labelOf(value), value === this.current));
  }

  /** 방향(LTR↔RTL) 전환 — 트리거 화살표를 라벨 반대편으로 재배치(글리프는 `▼` 유지). */
  setDirection(dir: TextDirection): void {
    this.dir = dir;
    this.trigger.setLabel(dropdownTriggerLabel(this.labelOf(this.current), this.dir));
  }

  /** 옵션 리스트 펼침 상태 — 호스트(SettingsModal)가 dim 클릭 닫기 가드에 사용(펼침 중엔 모달 안 닫음). */
  get isExpanded(): boolean {
    return this.expanded;
  }

  override setVisible(visible: boolean): this {
    super.setVisible(visible);
    (this as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(
      visible,
    );
    this.trigger.setVisible(visible);
    if (!visible) {
      this.collapse();
    }
    return this;
  }

  private toggle(): void {
    if (this.expanded) this.collapse();
    else this.expand();
  }

  private expand(): void {
    // 휴지 depth를 먼저 기억(아래 setDepth로 덮어쓰기 전) → 닫을 때 이 값으로 복원.
    this.restDepth = this.depth;
    this.expanded = true;
    const parent = this.parentContainer;
    if (parent) {
      // 모달(ModalLayer depth 2000+) 컨텍스트 — scene 직속 catcher(depth 40)는 모달 아래에 깔려
      // dim이 안 보이고 모달 overlay가 바깥클릭을 선점한다. catcher를 부모 Container에 넣어
      // bringToTop으로 "패널 위"에 올린 뒤, dropdown(this)을 그 위로 올려 catcher가 옵션 아래에 오게 한다.
      parent.add(this.catcher);
      this.catcher.setActive(true).setVisible(true);
      this.setRectInputEnabled(this.catcher, true);
      parent.bringToTop(this.catcher); // 패널 위(= dim)
    } else {
      // 비모달(scene 직속, 예: SortFilterControl) — 탭바(hudBar=30)·HUD콘텐츠(31) 위로 올리되
      // 모달 밴드(2000)·토스트(3000) 아래. catcher depth로 옵션 리스트(dropdownOverlay)보다 아래에 둔다.
      this.catcher.setDepth(DEPTH.dropdownDim).setActive(true).setVisible(true);
      this.setRectInputEnabled(this.catcher, true);
    }
    this.backDim.setVisible(true);
    this.frame.setVisible(true);
    for (const { btn } of this.optionButtons) btn.setVisible(true);
    this.setDepth(DEPTH.dropdownOverlay);
    // 중첩 Container(모달 contentContainer)에선 setDepth만으로 부모 내 형제 위로 안 올라옴 → bringToTop으로 강제.
    // (모달 경로) catcher bringToTop 뒤에 호출해 옵션 리스트가 catcher 위(= 최상단)에 오게 한다.
    parent?.bringToTop(this);
  }

  private collapse(): void {
    this.expanded = false;
    this.catcher.setActive(false).setVisible(false);
    this.setRectInputEnabled(this.catcher, false);
    // 모달 경로였으면 catcher를 부모에서 빼서 Dropdown 단독 소유로 복원(모달 일괄 destroy 대상 제외 → 이중 destroy 회피).
    // 비모달 경로는 catcher가 scene 직속이므로 depth를 바닥값 0으로 되돌린다(시각엔 무영향 — visible로 실질 제어).
    if (this.parentContainer) this.parentContainer.remove(this.catcher, false);
    else this.catcher.setDepth(0);
    this.backDim.setVisible(false);
    this.frame.setVisible(false);
    for (const { btn } of this.optionButtons) btn.setVisible(false);
    this.setDepth(this.restDepth);
  }

  private setRectInputEnabled(
    obj: Phaser.GameObjects.GameObject & { input?: { enabled: boolean } | null },
    enabled: boolean,
  ): void {
    if (obj.input) obj.input.enabled = enabled;
  }
}
